import { supabase } from "@/integrations/supabase/client";
import { getCurrentUserPricingSettings } from "@/lib/pricingSettings";

const DEFAULT_WASTE_PERCENT = 10;
const DEFAULT_PRICE_PER_SQUARE = 450;
const DEFAULT_TEAR_OFF_COST = 50;

interface RunJobMeasurementEstimateArgs {
  jobId: string;
  orgId: string;
  address: string;
  manualSquares?: number;
  lat?: number | null;
  lng?: number | null;
}

type JobMeasurementEstimateResult =
  | {
      manualRequired: true;
      address: string;
      lat?: number;
      lng?: number;
      message?: string;
    }
  | {
      manualRequired?: false;
      measurementId: string;
      estimateId: string;
      totalSquares: number;
      orderSquares: number;
      customerPrice: number;
      lineItemCount: number;
    };

export const runJobMeasurementEstimate = async ({ jobId, orgId, address, manualSquares, lat, lng }: RunJobMeasurementEstimateArgs): Promise<JobMeasurementEstimateResult> => {
  let measurement: any;

  if (manualSquares && manualSquares > 0) {
    const totalArea = manualSquares * 100;
    const approximateEdge = Math.round(Math.sqrt(totalArea) * 2);
    const { data: manualMeasurement, error: manualMeasurementError } = await supabase
      .from("measurements_roof")
      .insert({
        org_id: orgId,
        job_id: jobId,
        address,
        total_area: totalArea,
        total_squares: manualSquares,
        ridges: approximateEdge,
        hips: 0,
        valleys: 0,
        eaves: approximateEdge * 2,
        rakes: approximateEdge,
        waste_factor: DEFAULT_WASTE_PERCENT,
        facets: [],
        raw_response: { source: "Manual measurement fallback", approved_at: new Date().toISOString() },
      })
      .select()
      .single();

    if (manualMeasurementError) throw manualMeasurementError;
    measurement = manualMeasurement;

    if (lat && lng) {
      await supabase.from("jobs").update({ latitude: lat, longitude: lng }).eq("id", jobId).eq("org_id", orgId);
    }
  } else {
    const { data: measurementResponse, error: measurementError } = await supabase.functions.invoke(
      "google-solar-measurement",
      { body: { address, job_id: jobId } },
    );

    if (measurementError) throw measurementError;
    if (measurementResponse?.manual_required) return { manualRequired: true, address, lat: measurementResponse.lat, lng: measurementResponse.lng, message: measurementResponse.error };
    if (measurementResponse?.error) throw new Error(measurementResponse.error);

    measurement = measurementResponse?.measurement;
    if (!measurement?.id) throw new Error("Measurement completed but was not saved to this job");
  }

  const pricingSettings = await getCurrentUserPricingSettings();
  const wastePercent = Number(pricingSettings?.roof_base_waste_percent ?? DEFAULT_WASTE_PERCENT);
  const pricePerSquare = Number(pricingSettings?.roof_price_per_sq_asphalt ?? DEFAULT_PRICE_PER_SQUARE);
  const tearOffCost = Number(pricingSettings?.roof_tearoff_per_sq ?? DEFAULT_TEAR_OFF_COST);
  const dumpFee = Number(pricingSettings?.roof_dump_fee_per_sq ?? 0);
  const overheadPercent = Number(pricingSettings?.overhead_percent ?? 0);
  const profitPercent = Number(pricingSettings?.profit_margin_percent ?? 0);
  const measuredSquares = Number(measurement.total_squares || 0);
  const orderSquares = Math.ceil(measuredSquares * (1 + wastePercent / 100));
  const customerPrice = orderSquares * (pricePerSquare + tearOffCost + dumpFee);

  const { data: estimate, error: estimateError } = await supabase
    .from("job_estimates")
    .insert({
      org_id: orgId,
      job_id: jobId,
      measurement_id: measurement.id,
      status: "draft",
      label: "Estimate from Job Measurement",
      total_squares: orderSquares,
      waste_percent: wastePercent,
      material_rate: pricePerSquare,
      labor_rate: 0,
      tear_off_cost: tearOffCost,
      dump_fee: dumpFee,
      overhead_percent: overheadPercent,
      profit_percent: profitPercent,
      cost_subtotal: customerPrice,
      overhead_amount: 0,
      profit_amount: 0,
      customer_price: customerPrice,
    })
    .select("id")
    .single();

  if (estimateError) throw estimateError;

  const { data: catalogItems } = await supabase
    .from("catalog_items")
    .select("*")
    .eq("org_id", orgId)
    .eq("is_active", true);

  const { data: lineItemsData } = await supabase.functions.invoke("suggest-estimate-items", {
    body: { measurement_data: measurement, catalog_items: catalogItems || [], trade_code: "roofing", pricing_settings: pricingSettings },
  });

  if (lineItemsData?.suggested_items?.length) {
    const suggestedItems = lineItemsData.suggested_items.map((item: any, index: number) => ({
        estimate_id: estimate.id,
        item_name: item.item_name,
        description: item.description || null,
        quantity: item.quantity,
        unit_type: item.unit_type,
        material_cost: item.material_cost || 0,
        labor_cost: item.labor_cost || 0,
        total_cost: item.total_cost,
        category: item.category || "General",
        catalog_item_id: item.catalog_item_id || null,
        is_auto_generated: true,
        line_order: index,
      }));

    await supabase.from("estimate_line_items").insert(suggestedItems);

    const lineItemsTotal = suggestedItems.reduce((sum: number, item: any) => sum + Number(item.total_cost || 0), 0);
    if (lineItemsTotal > 0) {
      await supabase
        .from("job_estimates")
        .update({
          cost_subtotal: lineItemsTotal,
          profit_amount: 0,
          customer_price: lineItemsTotal,
        })
        .eq("id", estimate.id);
    }
  }

  await supabase.from("jobs").update({ estimated_value: customerPrice }).eq("id", jobId).eq("org_id", orgId);

  return {
    manualRequired: false,
    measurementId: measurement.id,
    estimateId: estimate.id,
    totalSquares: measuredSquares,
    orderSquares,
    customerPrice,
    lineItemCount: lineItemsData?.suggested_items?.length || 0,
  };
};